import { useMutation, useQuery, useQueryClient } from '@tanstack/vue-query';

import type { ApiResponse } from '@abaninja/types';

import { type DigitalCoach } from '../@types';
import {
  createDigitalCoach,
  deletePhoto,
  favorizeDigitalCoach,
  removeDigitalCoach,
  updateDigitalCoachProfile,
  uploadPhoto,
} from '../api';
import { keys } from '../keys';

export const useDigitalCoachProfile = () => useQuery(keys.digitalCoaches.me);

export const useUpdateDigitalCoachProfile = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: updateDigitalCoachProfile,
    onSuccess: (response) => {
      queryClient.setQueryData(keys.digitalCoaches.me.queryKey, response);
    },
  });
};

export const useUploadPhoto = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: uploadPhoto,
    onSuccess: (response) => {
      queryClient.setQueryData(keys.digitalCoaches.me.queryKey, response);
    },
  });
};

export const useDeletePhoto = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: deletePhoto,
    onSuccess: (response) => {
      queryClient.setQueryData(keys.digitalCoaches.me.queryKey, response);
    },
  });
};

export const useDigitalCoaches = () => useQuery(keys.digitalCoaches.list);

export const useCreateDigitalCoach = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: createDigitalCoach,
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: keys.digitalCoaches.list.queryKey });
    },
  });
};

export const useDeleteDigitalCoach = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: removeDigitalCoach,
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: keys.digitalCoaches.list.queryKey });
    },
  });
};

export const useFavorizeDigitalCoach = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: favorizeDigitalCoach,
    onSuccess: (response) => {
      queryClient.setQueryData<ApiResponse<DigitalCoach[]>>(keys.digitalCoaches.list.queryKey, (old) => {
        if (!old) {
          return undefined;
        }

        return {
          ...old,
          data: old.data.map((coach) => coach.isFavourite && response.data.isFavourite
            ? ({
              ...coach,
              isFavourite: false,
            })
            : coach)
            .map((coach) => coach.uuid === response.data.uuid
              ? ({
                ...coach,
                isFavourite: response.data.isFavourite,
              })
              : coach),
        };
      });
    },
  });
};
